import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import firebase from '../../../utilities/firebase';
import AddInvoiceDialog from '../../../components/AddInvoiceDialog';
import { StateManager } from '../../../utilities/stateManager';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });


const LINE_ITEMS = [
  { key: 'cashPrice', label: 'Cash Price' },
  { key: 'docFee', label: 'Doc Fee' },
  { key: 'titleFee', label: 'Title Fee' },
  { key: 'tagFee', label: 'Tag Fee' },
  { key: 'conciergeFee', label: 'Concierge Fee' },
  { key: 'salesTax', label: 'Sales Tax' },
  { key: 'surtax', label: 'Surtax' },
];

export default function InvoicePanel({ invoices = [], deal = {}, stockNumber, disabled, onInvoiceSelectionChange, tradeDeduction = 0 }) {
  const [localInvoices, setLocalInvoices] = React.useState(invoices);
  const [activeId, setActiveId] = React.useState(deal.invoice);
  const [addOpen, setAddOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingInvoice, setEditingInvoice] = React.useState(null);
  const [showOthers, setShowOthers] = React.useState(false);

  React.useEffect(() => { setLocalInvoices(invoices); }, [invoices]);
  React.useEffect(() => { setActiveId(deal.invoice); }, [deal.invoice]);

  const activeInvoice = localInvoices.find(inv => inv.id === activeId) || null;
  const otherInvoices = localInvoices.filter(inv => inv.id !== activeId);

  const selectInvoice = async (inv) => {
    await firebase.firestore().doc('deals/' + stockNumber).set({ invoice: inv.id }, { merge: true });
    setActiveId(inv.id);
    onInvoiceSelectionChange?.(inv);
  };

  const deleteInvoice = async (invoiceId, e) => {
    e?.stopPropagation();
    if (!StateManager.isAdmin()) return;
    await firebase.firestore().collection('invoices').doc(invoiceId).delete();
    const updated = localInvoices.filter(inv => inv.id !== invoiceId);
    setLocalInvoices(updated);
    if (invoiceId === activeId) {
      setActiveId(null);
      onInvoiceSelectionChange?.({});
      await firebase.firestore().doc('deals/' + stockNumber).set({ invoice: null }, { merge: true });
    }
  };

  const refreshAndSelect = async (inv) => {
    const snap = await firebase.firestore().collection('invoices').where('stock', '==', stockNumber).get();
    const fresh = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setLocalInvoices(fresh);
    const found = fresh.find(x => x.id === inv.id);
    if (found) { setActiveId(found.id); onInvoiceSelectionChange?.(found); }
  };

  const lineItems = activeInvoice
    ? LINE_ITEMS.filter(item => activeInvoice[item.key])
    : [];

  return (
    <Box>
      {activeInvoice ? (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="overline" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700 }}>
              Active Invoice
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              {!disabled && (
                <IconButton size="small" onClick={() => { setEditingInvoice(activeInvoice); setEditOpen(true); }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {StateManager.isAdmin() && (
                <IconButton size="small" color="error" onClick={(e) => deleteInvoice(activeInvoice.id, e)}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.75 }}>
            {lineItems.map((item) => (
              <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.35 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.label}</Typography>
                <Typography variant="body2">{USD(activeInvoice[item.key])}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{USD(activeInvoice.total)}</Typography>
            </Box>
          </Box>

          {!disabled && (
            <Box sx={{ mt: 1.5 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                New Invoice
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>No invoice selected</Typography>
          {!disabled && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Create Invoice
            </Button>
          )}
        </Box>
      )}

      {otherInvoices.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Box
            onClick={() => setShowOthers(!showOthers)}
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {otherInvoices.length} other invoice{otherInvoices.length > 1 ? 's' : ''}
            </Typography>
            {showOthers ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={showOthers}>
            {otherInvoices.map(inv => (
              <Box
                key={inv.id}
                sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', '&:hover': { bgcolor: 'grey.50' } }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{USD(inv.total)}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Price: {USD(inv.cashPrice)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="outlined" onClick={() => selectInvoice(inv)} disabled={disabled}>
                    Select
                  </Button>
                  {StateManager.isAdmin() && (
                    <IconButton size="small" color="error" onClick={(e) => deleteInvoice(inv.id, e)}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Collapse>
        </Box>
      )}

      <AddInvoiceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        stockNumber={stockNumber}
        type="sales"
        tax_deduction={tradeDeduction}
        onInvoiceAdded={async (inv) => { await refreshAndSelect(inv); setAddOpen(false); }}
      />
      <AddInvoiceDialog
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditingInvoice(null); }}
        stockNumber={stockNumber}
        type="sales"
        tax_deduction={tradeDeduction}
        onInvoiceAdded={async (inv) => { await refreshAndSelect(inv); setEditOpen(false); setEditingInvoice(null); }}
        editMode
        invoiceData={editingInvoice}
      />
    </Box>
  );
}
